import { Menu } from '@mui/material';
import React, { useCallback, useState } from 'react';
import Datepicker from 'react-datepicker';
import { AiOutlinePlus } from 'react-icons/ai';
import { BiChevronDown } from 'react-icons/bi';
import { MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Table } from 'src/components';
import Button from 'src/components/button';
import CrudIcon from 'src/components/crud-icon';
import { fetchExpenses } from 'src/redux/action/expenses.action';
import { RootState } from 'src/redux/reducers';
import Service from 'src/setup/network';
import { oneWeekAgoDate } from 'src/utils/dateOneMonthAgo';
import AddExpenses from './crud/AddExpenses';
import { formatDateNext, formatTime } from '../kds';
import { PaymentStatusButton } from 'src/components/payment-status';
import { CurrencyAmount } from 'src/containers/currency';

interface IProps {}

type ExpenseFilters = {
  page: number;
  limit: number;
  sortBy: 'ASC' | 'DESC';
  paymentMethodIds: string;
  search: string;
  from: string | null;
  to: string | null;
};

const Expenses: React.FC<IProps> = ({}) => {
  const [showModal, setShowModal] = React.useState(true);
  const [qrData, setQrData] = React.useState<{ id: string; name: string }[]>(
    []
  );
  const [isEdit, setIsEdit] = React.useState(false);
  const [editId, setEditId] = React.useState('');
  const [editExpensesData, setEditExpensesData] = React.useState([]);
  const initialFilters: ExpenseFilters = {
    page: 1,
    limit: 5,
    sortBy: 'DESC',
    paymentMethodIds: '',
    search: '',
    from: oneWeekAgoDate().toISOString(),
    to: new Date().toISOString(),
  };
  const [filters, setFilters] = useState<ExpenseFilters>(initialFilters);
  const { expensesData, expensesPagination, expensesTotal, loading } =
    useSelector(({ expensesData }: RootState) => expensesData);
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<{ id: string; name: string }[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openDisc = Boolean(anchorEl);

  const generateRandomDigits = (e: any) => {
    e.preventDefault();
    const randomDigits = [];
    for (let i = 0; i < 5; i++) {
      const randomDigit = Math.floor(Math.random() * 10);
      randomDigits.push(randomDigit);
    }
    setToken(randomDigits.join(''));
  };
  const dispatch = useDispatch();

  const fetchQrData = async () => {
    try {
      const response = await Service.get(`/settings/paymentMethod`);
      const data = await response?.data?.qrs;
      setQrData(data);
    } catch (error: any) {
      throw new Error('Failed to fetch qr data');
    }
  };

  React.useEffect(() => {
    dispatch(fetchExpenses(filters) as any);
    fetchQrData();
  }, [dispatch, filters]);

  const handleFilterChange = <K extends keyof ExpenseFilters>(
    key: K,
    value: ExpenseFilters[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key !== 'page' && { page: 1 }),
    }));
  };

  const handleAddModal = () => {
    setToken('');
    setIsEdit(false);
    setEditId('');
    setShowModal(false);
    setTimeout(() => {
      setShowModal(true);
    }, 100);
    setEditExpensesData([]);
  };

  const handleModal = () => {
    setShowModal(!showModal);
  };

  const handleEdit = (data: any) => {
    setEditExpensesData(data);
    setIsEdit(true);
    setEditId(data.id);
    setShowModal(false);
    setTimeout(() => {
      setShowModal(true);
    }, 100);
  };

  const fetchData = useCallback(() => {
    dispatch(fetchExpenses(filters) as any);
  }, [dispatch, filters]);

  // table column
  const tableColumn = [
    {
      Header: 'Date',
      Cell: (row: any) => (
        <div className="flex flex-col">
          <div className="text-sm">
            {formatDateNext(row?.cell?.row?.original?.date)}
          </div>
          <div className="text-xs text-light-grey">
            {formatTime(row?.cell?.row?.original?.date)}
          </div>
        </div>
      ),
    },
    {
      Header: 'Name',
      flex: 1,
      accessor: 'name',
    },
    { Header: 'Quantity', accessor: 'quantity' },
    { 
      Header: 'Amount',
      Cell: (row: any) => {
        return(
          <div className="flex flex-col gap-1">
            <span className="text-sm"><CurrencyAmount amount={row?.cell?.row?.original?.amount}/></span>
            {row?.cell?.row?.original?.remainingAmount>0 && <span className='text-xs text-light-grey'>Remaining: <CurrencyAmount amount={row?.cell?.row?.original?.remainingAmount}/></span>}
          </div>
        )
      },
     },
    {
      Header: 'Method',
      Cell: (row: any) => row?.cell?.row?.original?.payments?.map((each: any) => each.method?.name).join(', ')
    },
    { 
      Header: 'Status',
      Cell: (row: any) => {
        return PaymentStatusButton(row?.cell?.row?.original?.paymentStatus);
      },
     },
    {
      Header: 'Action',
      Cell: (row: any) => (
        <CrudIcon
          fetchData={fetchData}
          url="/expenses"
          data={row?.cell?.row?.original}
        >
          <div
            className="w-7 h-7 rounded-xl bg-light-grey bg-opacity-50 flex items-center justify-center"
            onClick={() => handleEdit(row?.cell?.row?.original)}
          >
            <MdEdit color="#4062FF" />
          </div>
        </CrudIcon>
      ),
    },
  ];

  const DateRangePicker = () => {
    return (
      <div className="text-black flex flex-row items-center">
        <Datepicker
          name="date"
          className="w-40 xl:w-[fit] appearance-none rounded-xl text-text-black
                placeholder:capitalize py-3 block placeholder:text-sm px-3 text-sm
                  focus-within:outline-0 border-solid border border-primary"
          locale="en-US"
          selected={filters.from ? new Date(filters.from) : null}
          onChange={(date) =>
            handleFilterChange('from', date ? date.toISOString() : null)
          }
          startDate={filters.from ? new Date(filters.from) : undefined}
          endDate={filters.to ? new Date(filters.to) : undefined}
          selectsStart
          showTimeSelect
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="Select start date"
        />
        <span className="mx-1">-</span>
        <Datepicker
          name="date"
          className="w-40 xl:w-[fit] appearance-none rounded-xl text-text-black
                  placeholder:capitalize py-3 block placeholder:text-sm px-3 text-sm
                  focus-within:outline-0 border-solid border border-primary"
          locale="en-US"
          selected={filters.to ? new Date(filters.to) : null}
          onChange={(date) =>
            handleFilterChange('to', date ? date.toISOString() : null)
          }
          selectsEnd
          showTimeSelect
          startDate={filters.from ? new Date(filters.from) : undefined}
          endDate={filters.to ? new Date(filters.to) : undefined}
          dateFormat="MMM d, yyyy h:mm aa"
          placeholderText="Select end date"
        />
      </div>
    );
  };

  const handleClickDiscMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDiscMenu = () => {
    setAnchorEl(null);
  };

  const SearchDiv = (
    <Search
      placeholder="search expenses"
      query={filters.search}
      setQuery={(val) => handleFilterChange('search', val)}
    />
  );

  const AddExpensesBtn = (
    <button
      onClick={handleAddModal}
      className={`bg-primary text-pure-white rounded-xl px-2 py-3 capitalize font-semibold`}
    >
      <div className="flex items-center  justify-center">
        <AiOutlinePlus size={16} />
        <p className="ml-1 text-xs xl:text-sm font-semibold">Add Expense</p>
      </div>
    </button>
  );

  const Filters = (
    <div className="flex xl:hidden flex-col items-end gap-4">
      <div className="flex items-center gap-4">
        <div className="xl:hidden">
          <DateRangePicker />
        </div>
        {AddExpensesBtn}
      </div>
    </div>
  );

  const XLFilters = (
    <div className="hidden xl:flex flex-row items-center gap-4 justify-end">
      <div className="flex items-center gap-4 justify-end">
        {SearchDiv}
        <DateRangePicker />
      </div>
      <div className="flex flex-row justify-end gap-4">{AddExpensesBtn}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {XLFilters}
      {Filters}
      <div className="flex flex-row gap-4 items-center justify-end mt-2">
        <div className="xl:hidden">{SearchDiv}</div>
        <Button
          className="self-end"
          text={
            <div className="flex items-center justify-between w-[160px] xl:w-[200px]">
              <span
                className="block whitespace-nowrap overflow-hidden text-ellipsis text-sm max-w-[200px]"
                title={status.map((each: any) => each.name).join(', ')}
              >
                {status.map((each: any) => each.name).join(', ') ||
                  'Select payment method'}
              </span>
              <BiChevronDown size={18} />
            </div>
          }
          id="basic-button"
          aria-controls={openDisc ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openDisc ? 'true' : undefined}
          // @ts-ignore
          onClick={handleClickDiscMenu}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={openDisc}
          onClose={handleCloseDiscMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          PaperProps={{
            style: {
              boxShadow: 'none',
              marginTop: '5px',
              border: '1px solid #4062FF',
              height: 'fit-content',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
            },
          }}
        >
          <div
            className="font-Outfit px-2 py-2 hover:bg-almost-white text-[14px] cursor-pointer text-primary"
            onClick={() => {
              setStatus(qrData);
              setFilters((prev) => ({
                ...prev,
                paymentMethodIds: qrData.map((s: any) => s.id).join(','),
              }));
            }}
          >
            Select All
          </div>
          <div
            className="font-Outfit px-2 py-2 hover:bg-almost-white text-[14px] cursor-pointer text-red-500"
            onClick={() => {
              setStatus([]);
              setFilters((prev) => ({ ...prev, paymentMethodIds: '' }));
            }}
          >
            Clear All
          </div>
          {qrData?.map((each: any, i: number) => {
            const isSelected = status.some((item: any) => item.id === each.id);
            return (
              <div
                key={'payment' + i}
                className={`font-Outfit px-2 py-2 hover:bg-almost-white font-normal text-[14px] leading-6 cursor-pointer ${
                  isSelected ? 'bg-blue-100' : ''
                }`}
                onClick={() => {
                  const isSelected = status.some((s: any) => s.id === each.id);
                  const newStatus = isSelected
                    ? status.filter((s: any) => s.id !== each.id)
                    : [...status, each];

                  setStatus(newStatus);
                  const paymentMethodIds = newStatus
                    .map((s: any) => s.id)
                    .join(',');
                  setFilters((prev) => ({ ...prev, paymentMethodIds }));
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  className="mr-2"
                />
                {each.name}
              </div>
            );
          })}
        </Menu>
      </div>
      <div className="flex text-black gap-2 text-xl justify-end font-semibold">
        <span>Total:</span>
        <span>{expensesTotal}</span>
      </div>
      <Table
        loading={loading}
        tableColumn={tableColumn}
        tableData={expensesData}
        pagination={true}
        className='mt-0'
        serverPagination={{
          pageIndex: filters.page - 1, // 0-based index for table
          pageSize: filters.limit,
          totalPages: expensesPagination.totalPages,
          setPageIndex: (page: number) =>
            setFilters((prev) => ({ ...prev, page: page + 1 })), // back to 1-based
          setPageSize: (limit: number) =>
            setFilters((prev) => ({ ...prev, limit, page: 1 })), // reset to page 1
        }}
      />
      <AddExpenses
        generateRandomDigits={generateRandomDigits}
        token={token}
        editExpensesData={editExpensesData}
        handleModal={handleModal}
        showModal={showModal}
        editId={editId}
        isEdit={isEdit}
        setEditId={setEditId}
        setIsEdit={setIsEdit}
        qrData={qrData}
        filters={filters}
      />
    </div>
  );
};

export default React.memo(Expenses);
